import React from 'react';
import classNames from 'classnames/bind';
import Text from '../../../../../atoms/Text';
import Icon from '../../../../../atoms/Icon';
import Stack from '../../../../../atoms/Stack';
import Button from '../../../../../atoms/button';
import EventTimer from '../../../EventTimer';
import properties from '../../../../../../modules/ajio-util/properties';
import { GRAND_PAGE_HEADER } from '../GrandPrize/GrandPrizeConstant';
import maskName from '../../../utils/maskName';
import formatPrice from '../../../utils/formatPrice';

import styles from './index.module.pcss';

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
const imageBaseUrl: string = properties.getProperty('imageBaseUrl');
const trending = `${imageBaseUrl}/static/img/trending.svg`;

interface List {
  name: string;
  rank: number;
  uuid: string;
  mobile: string;
  amount: string;
}

type LeaderBoardType = 'grand' | 'normal';
interface LeaderBoard {
  className?: string;
  renderGrandPrizeHeader?: () => JSX.Element | null;
  renderFooterText?: () => JSX.Element | null;
  type?: LeaderBoardType;
  list: List[];
  currentUser: List;
  eventEndsIn?: number;
  highlightUser: boolean;
  showWinner?: boolean;
  shopMoreUrl: string;
}

const cx = classNames.bind(styles);

const getText = (rank: number): string => {
  let text = 'You can be a winner !!!';
  if (rank === 1) {
    text = 'You are winning ! ';
  } else if (rank <= 5 && rank !== 0) {
    text = 'You are among the top 5 shoppers !!!';
  }
  return text;
};

const updateList = (
  list: List[],
  currentUser: List,
  type = 'normal',
  highlightUser: boolean
): List[] => {
  const { uuid: currentUuid } = currentUser || {};
  // when no user is logged in
  if (!currentUuid && type.toLowerCase() !== 'grand' && highlightUser) {
    const guestList: List = {
      name: 'Hello Guest',
      mobile: '',
      rank: 0,
      uuid: 'guest'
    };
    return [...list, guestList];
  }
  // when user is not in list
  if (
    list?.findIndex(({ uuid }) => uuid === currentUuid) === -1 &&
    type.toLowerCase() !== 'grand' &&
    highlightUser
  ) {
    const guestList: List = {
      name: 'Hello There',
      mobile: '',
      rank: 0,
      uuid: currentUuid
    };
    return [...list, guestList];
  }
  return list;
};
const amountUI = (rank: number, price: string): JSX.Element => {
  const amountClass = cx('amountCommonStyle', {
    firstRankAmount: price && rank === 1,
    otherAmount: price && rank !== 1
    // noAmount: !price
  });
  // if (!price) return null;
  return <div className={amountClass}>{price ? formatPrice(price) : ''}</div>;
};

const renderListItem = (
  { rank, name, mobile, saleType, postDataType, amount }: List,
  index: number,
  type: LeaderBoardType = 'normal'
): JSX.Element => (
  <Stack
    widths={[25, 75]}
    className={cx(
      rank === 1 && (saleType === 'POST_SALE' || postDataType === 'POST_SALE') ? 'active_Block' : ''
    )}
  >
    {rank === 1 && (saleType === 'POST_SALE' || postDataType === 'POST_SALE') ? (
      <div className={cx('post-winner')}>
        <img className={cx('trendingImage')} src={trending} alt="Trending icon" />
        <Text variant="T3" weight="bold">
          {type === 'grand' ? index + 1 : rank || index + 1}
        </Text>
      </div>
    ) : (
      <div className={cx('lhs_inside')}>
        <Text>{type === 'grand' ? index + 1 : rank || index + 1}</Text>
      </div>
    )}
    <div className={cx('rhs_inside')}>
      <div>
        <Text
          color="Nero"
          weight={
            rank === 1 && (saleType === 'POST_SALE' || postDataType === 'POST_SALE')
              ? 'bold'
              : 'regular'
          }
        >
          {`${maskName(name)}`}
        </Text>
      </div>
      <div className={cx('mobile')}>
        <Text color="Light_Grey" variant="T1">{`${mobile}`}</Text>
      </div>
      {rank === 1 && (saleType === 'POST_SALE' || postDataType === 'POST_SALE') && (
        <div className={cx('winner')}>
          <Text color="Jade" weight="bold">
            Winner !!!
          </Text>
        </div>
      )}
    </div>
    {type !== 'grand' ? amountUI(rank, amount) : null}
  </Stack>
);

const renderCurrentUserItem = (
  { rank, name, mobile }: List,
  index: number,
  eventEndsIn: number,
  shopMoreUrl: string,
  type: LeaderBoardType = 'normal'
): JSX.Element => {
  return (
    <Stack widths={[25, 75]} className={cx('active_Block')}>
      <div className={cx('lhs_inside')}>
        {rank ? (
          <div className={cx('lhs_trending')}>
            <img className={cx('trendingImage')} src={trending} alt="Trending icon" />
            <Text variant="T3">{type === 'grand' ? index + 1 : rank || index + 1}</Text>
          </div>
        ) : null}
        {rank === 0 ? <img src={trending} alt="Trending icon" /> : null}
      </div>
      <div className={cx('rhs_inside')}>
        <div>
          <Text color="Nero" weight="bold" variant="T3">{`${name}`}</Text>
        </div>
        <div className={cx('mobile')}>
          <Text color="Light_Grey" variant="T1">{`${mobile}`}</Text>
        </div>
        <Text color="Jade" weight="bold">
          {getText(rank)}
        </Text>
        <div className={cx('shopmore')}>
          <a href={shopMoreUrl} className={cx('event-anchor-cta')}>
            <Button btnLabel="Shop & Get Rewards" />
          </a>
        </div>
        {eventEndsIn > 0 && (
          <EventTimer
            variant="T2"
            weight="bold"
            color="Salmon"
            title="Event Ends in"
            eventDate={eventEndsIn}
            onTimerComplete={() => console.log('timer end')}
          />
        )}
      </div>
    </Stack>
  );
};

const renderWinnerUserItem = (
  { rank, name, mobile, amount }: List,
  type: LeaderBoardType = 'normal'
): JSX.Element => {
  return (
    <Stack widths={[25, 75]} className={cx('active_Block')}>
      <div className={cx('lhs_inside')}>
        <div className={cx('lhs_trending')}>
          <img className={cx('trendingImage')} src={trending} alt="Trending icon" />
          <Text variant="T3">{rank}</Text>
        </div>
      </div>
      <div className={cx('rhs_inside')}>
        <div>
          <Text color="Nero" weight="bold" variant="T3">{`${name}`}</Text>
        </div>
        <div className={cx('mobile')}>
          <Text color="Light_Grey" variant="T1">{`${mobile}`}</Text>
        </div>
        <Text color="Jade" weight="bold">
          Winner !!!
        </Text>
      </div>
      {type !== 'grand' ? amountUI(rank, amount) : null}
    </Stack>
  );
};

const renderRankHeader = (): JSX.Element => {
  return (
    <Stack widths={[25, 75]}>
      <div className={cx('lhs')}>
        <Text weight="bold" variant="T3">
          Rank
        </Text>
      </div>
      <div className={cx('rhs')}>
        <Text weight="bold" variant="T3">
          Name
        </Text>
      </div>
    </Stack>
  );
};

function LeaderBoard(props: LeaderBoard): JSX.Element {
  const {
    className,
    list,
    currentUser,
    type,
    renderGrandPrizeHeader,
    renderFooterText,
    eventEndsIn,
    highlightUser,
    showWinner,
    saleType,
    postDataType,
    shopMoreUrl
  } = props;
  const finalList = updateList(list, currentUser, type, highlightUser);

  return (
    <div className={cx('container', className)}>
      {!renderGrandPrizeHeader ? renderRankHeader() : renderGrandPrizeHeader()}
      {!list.length && type?.toLowerCase() !== 'grand' ? (
        <div className={cx('info-alert')}>
          <Icon icon="icon-Info" iconClass={cx('info-iconclass')} />
          <Text variant="T2" color="Nero" weight="regular">
            {GRAND_PAGE_HEADER.noUserText}
          </Text>
        </div>
      ) : null}
      {finalList?.map(({ rank, name, mobile, uuid, amount }, index) => {
        if (showWinner && rank === 1 && uuid === currentUser?.uuid) {
          return renderWinnerUserItem({ rank, name, mobile, uuid, amount }, type);
        }
        if (highlightUser && (uuid === currentUser?.uuid || uuid === 'guest')) {
          return renderCurrentUserItem(
            { rank, name, mobile, uuid, amount },
            index,
            eventEndsIn,
            shopMoreUrl,
            type
          );
        }
        return renderListItem(
          { rank, name, mobile, uuid, saleType, postDataType, amount },
          index,
          type
        );
      })}
      {renderFooterText ? renderFooterText() : null}
    </div>
  );
}

LeaderBoard.defaultProps = {
  className: '',
  renderGrandPrizeHeader: null,
  renderFooterText: null,
  eventEndsIn: 0,
  type: 'normal',
  showWinner: false
};

export default LeaderBoard;
